import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../../config/api";
import "./RegPulseFeedback.css";
import { trackEvent } from "../../config/analytics";

const UNSUBSCRIBE_API_URL = `${API_BASE_URL}/feedback`;
const FEEDBACK_REASON_API_URL = `${API_BASE_URL}/feedbackReasonList`;

type FeedbackReason = {
  id: number;
  name: string;
};

type UnsubscribePayload = {
  token: string;
  reason: any;
  other: string;
};

const RegPulseFeedback = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const token = params.token || searchParams.get("token") || "";

  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const [feedbackReasons, setFeedbackReasons] = useState<FeedbackReason[]>([]);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [apiError, setApiError] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [isReasonLoading, setIsReasonLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);

  useEffect(() => {
    const fetchFeedbackReasons = async () => {
      try {
        setIsReasonLoading(true);
        setReasonError("");

        const response = await axios.get<FeedbackReason[]>(
          FEEDBACK_REASON_API_URL
        );
        setFeedbackReasons(response.data);
      } catch (error) {
        console.error("Error fetching feedback reasons:", error);
        setReasonError("Unable to load feedback reasons. Please try again.");
      } finally {
        setIsReasonLoading(false);
      }
    };

    fetchFeedbackReasons();
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token) {
      setError("Invalid unsubscribe link. Token is missing.");
      return;
    }

    // if (selectedReasonId === null) {
    //   setError("Please select one reason");
    //   return;
    // }

    try {
      setError("");
      setApiError("");
      setIsSubmitting(true);

      const payload: UnsubscribePayload = {
        token: token,
        reason: selectedReasonId,
        other: comment.trim(),
      };

      const response = await axios.post(UNSUBSCRIBE_API_URL, payload);

      if (response?.data?.code === "200") {
        trackEvent("unsubscribe_feedback_submitted");
        setIsFeedbackSubmitted(true);
        return;
      }
      navigate("/regpulseunsubscribeerror");
      


    } catch (error) {
      console.error("Error submitting unsubscribe feedback:", error);

      if (axios.isAxiosError(error)) {
        setApiError(
          error.response?.data?.message ||
            "Unable to submit your feedback. Please try again."
        );
      } else {
        setApiError("Unable to submit your feedback. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubscribeAgain = () => {
    navigate("/subscribe");
  };

  return (
    <section className="regpulse-feedback-page">
      <form className="regpulse-feedback-card" onSubmit={handleSubmit}>
          <div className="unsubscribe-wrapper">
      <div className="unsubscribe-card">
        <div className="success-icon">
          ✓
        </div>

        <h2 className="unsubscribe-title">
          You have been unsubscribed
        </h2>

        <p className="unsubscribe-description">
          You have been successfully unsubscribed from{" "}
          <strong>EvoNext RegPulse Email Digest</strong> service and you won't
          receive any further mails.
        </p>

        <button
          type="button"
          className="subscribe-again-btn"
          onClick={handleSubscribeAgain}
        >
          Unsubscribed mistakenly? Subscribe again →
        </button>
      </div>
    </div>
        {/* <div className="regpulse-feedback-header">


          <div>
            <p className="regpulse-feedback-eyebrow">EvoNext RegPulse</p>
            <h1>Before you go - quick feedback</h1>
            <p>This helps us improve Evonext RegPulse for everyone.</p>
          </div>
        </div> */}

        {isFeedbackSubmitted ? (
          <div className="regpulse-feedback-thank-you" role="status">
            <div className="success-icon" aria-hidden="true" />
            <h2>Thanks for the feedback</h2>
            <p>It helps us make EvoNext RegPulse better.</p>
            <button
              type="button"
              className="regpulse-home-btn"
              onClick={() => navigate("/home")}
            >
              Go to www.evonext.ai <span aria-hidden="true">&rarr;</span>
            </button>
          </div>
        ) : (
          <div className="regpulse-feedback-body">
          <label className="regpulse-feedback-question">
            Why are you unsubscribing? <span>*</span>
          </label>

          <div className="regpulse-feedback-options">
            {isReasonLoading && (
              <p className="regpulse-feedback-error">Loading feedback reasons...</p>
            )}

            {!isReasonLoading && reasonError && (
              <p className="regpulse-feedback-error">{reasonError}</p>
            )}

            {!isReasonLoading && feedbackReasons.map((option) => (
              <label
                key={option.id}
                className={`regpulse-feedback-option ${
                  selectedReasonId === option.id ? "selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name="unsubscribeReason"
                  value={option.id}
                  checked={selectedReasonId === option.id}
                  onChange={() => setSelectedReasonId(option.id)}
                />
                <span>{option.name}</span>
              </label>
            ))}
          </div>

          {error && <p className="regpulse-feedback-error">{error}</p>}

          <label htmlFor="feedbackComment" className="regpulse-comment-label">
            Anything else you'd like to share? <span>(optional)</span>
          </label>

          <textarea
            id="feedbackComment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Optional comments..."
            className="regpulse-feedback-textarea"
          />

          {apiError && <p className="regpulse-feedback-error">{apiError}</p>}

          <button
            type="submit"
            className="regpulse-feedback-submit"
            disabled={isSubmitting || isReasonLoading || feedbackReasons.length === 0 || (selectedReasonId === null && comment.length === 0)}
          >
            {isSubmitting ? "Submitting..." : "Submit feedback"}
          </button>
            <p className="regpulse-feedback-note">
              Submitting this feedback won't change your unsubscribed status.
            </p>
          </div>
        )}
      </form>
    </section>
  );
};

export default RegPulseFeedback;
